import React, { useState, useEffect } from 'react';
import { Save, Loader2, CheckCircle, FileText, ChevronDown, ChevronUp } from 'lucide-react';
import MediaUploader from '../../components/MediaUploader';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PAGE_SCHEMAS = [
  {
    id: 'homepage',
    label: 'Homepage',
    fields: [
      { key: 'hero_label', label: 'Hero Label', type: 'text', placeholder: 'Third Culture Leadership' },
      { key: 'hero_title', label: 'Hero Title', type: 'text', placeholder: 'Cultural fluency, taught with clarity.' },
      { key: 'hero_subtitle', label: 'Hero Subtitle', type: 'textarea', placeholder: 'Practical frameworks...' },
      { key: 'hero_image', label: 'Hero Image (Right side)', type: 'image' },
      { key: 'premise_title', label: 'Premise Title', type: 'text', placeholder: 'Cultural Fluency Without Compromise' },
      { key: 'premise_body', label: 'Premise Body', type: 'textarea' },
    ]
  },
  {
    id: 'books',
    label: 'Books Page',
    fields: [
      { key: 'book_title', label: 'Book Title', type: 'text', placeholder: 'Cultural Fluency Without Compromise' },
      { key: 'book_subtitle', label: 'Subtitle', type: 'text', placeholder: 'Thriving Across Cultures...' },
      { key: 'book_description', label: 'Description', type: 'textarea' },
      { key: 'book_cover', label: 'Book Cover Image', type: 'image' },
      { key: 'book_isbn', label: 'ISBN', type: 'text' },
      { key: 'book_order_url', label: 'Order URL', type: 'text' },
      { key: 'book_apple_url', label: 'Apple Books URL', type: 'text' },
      { key: 'book_kindle_url', label: 'Kindle URL', type: 'text' },
    ]
  },
  {
    id: 'courses',
    label: 'Courses Page',
    fields: [
      { key: 'courses_title', label: 'Page Title', type: 'text', placeholder: 'Self-Paced Course' },
      { key: 'courses_subtitle', label: 'Subtitle', type: 'textarea' },
      { key: 'module1_title', label: 'Module 1 Title', type: 'text' },
      { key: 'module1_desc', label: 'Module 1 Description', type: 'textarea' },
      { key: 'module2_title', label: 'Module 2 Title', type: 'text' },
      { key: 'module2_desc', label: 'Module 2 Description', type: 'textarea' },
      { key: 'module3_title', label: 'Module 3 Title', type: 'text' },
      { key: 'module3_desc', label: 'Module 3 Description', type: 'textarea' },
      { key: 'module4_title', label: 'Module 4 Title', type: 'text' },
      { key: 'module4_desc', label: 'Module 4 Description', type: 'textarea' },
    ]
  },
  {
    id: 'speaking',
    label: 'Speaking Page',
    fields: [
      { key: 'speaking_title', label: 'Page Title', type: 'text' },
      { key: 'speaking_intro', label: 'Introduction', type: 'textarea' },
      { key: 'speaking_topics', label: 'Topics (one per line)', type: 'textarea' },
      { key: 'speaking_image', label: 'Speaker Image', type: 'image' },
    ]
  },
  {
    id: 'about',
    label: 'About Page',
    fields: [
      { key: 'about_title', label: 'Page Title', type: 'text' },
      { key: 'about_intro', label: 'Introduction', type: 'textarea' },
      { key: 'about_bio', label: 'Full Bio', type: 'textarea' },
      { key: 'about_image', label: 'Profile Image', type: 'image' },
    ]
  },
  {
    id: 'contact',
    label: 'Contact Page',
    fields: [
      { key: 'contact_title', label: 'Page Title', type: 'text' },
      { key: 'contact_subtitle', label: 'Subtitle', type: 'textarea' },
      { key: 'contact_email', label: 'Contact Email', type: 'text' },
      { key: 'contact_phone', label: 'Phone', type: 'text' },
    ]
  },
];

const AdminPages = () => {
  const { token } = useAuth();
  const [pages, setPages] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [saved, setSaved] = useState({});
  const [openPage, setOpenPage] = useState('homepage');

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const res = await fetch(`${API_URL}/api/cms/pages`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) {
          const data = await res.json();
          const map = {};
          (data.pages || []).forEach(p => { map[p.id] = p.content || {}; });
          setPages(map);
        }
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    fetchAll();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateField = (pageId, key, value) => {
    setPages(prev => ({
      ...prev,
      [pageId]: { ...(prev[pageId] || {}), [key]: value }
    }));
  };

  const savePage = async (pageId) => {
    setSaving(prev => ({ ...prev, [pageId]: true }));
    try {
      await fetch(`${API_URL}/api/cms/pages/${pageId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ content: pages[pageId] || {} }),
      });
      setSaved(prev => ({ ...prev, [pageId]: true }));
      setTimeout(() => setSaved(prev => ({ ...prev, [pageId]: false })), 2000);
    } catch (e) { console.error(e); }
    setSaving(prev => ({ ...prev, [pageId]: false }));
  };

  const inputClass = "w-full bg-navy border border-cream/10 rounded-lg px-3 py-2 text-sm text-cream placeholder-cream/20 focus:outline-none focus:ring-1 focus:ring-gold/30";

  if (loading) return <div className="py-16 text-center text-cream/20">Loading...</div>;

  return (
    <div data-testid="admin-pages">
      <div className="mb-6">
        <h1 className="font-serif text-2xl text-cream font-semibold">Pages</h1>
        <p className="text-cream/30 text-sm mt-1">Edit content for public pages</p>
      </div>

      <div className="space-y-3">
        {PAGE_SCHEMAS.map((schema) => (
          <div key={schema.id} className="bg-navy border border-gold/[0.06] rounded-xl overflow-hidden" data-testid={`page-editor-${schema.id}`}>
            {/* Header */}
            <button
              onClick={() => setOpenPage(openPage === schema.id ? null : schema.id)}
              className="w-full flex items-center justify-between px-5 py-4 hover:bg-white/[0.02] transition-colors"
            >
              <div className="flex items-center gap-3">
                <FileText size={16} className="text-gold/40" />
                <span className="text-cream text-sm font-medium">{schema.label}</span>
                <span className="text-cream/15 text-[10px]">{schema.fields.length} fields</span>
              </div>
              <div className="flex items-center gap-2">
                {saved[schema.id] && <span className="text-green-400 text-[10px] flex items-center gap-1"><CheckCircle size={10} /> Saved</span>}
                {openPage === schema.id ? <ChevronUp size={16} className="text-cream/30" /> : <ChevronDown size={16} className="text-cream/30" />}
              </div>
            </button>

            {/* Fields */}
            {openPage === schema.id && (
              <div className="px-5 pb-5 border-t border-cream/[0.04] pt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {schema.fields.map((field) => (
                    <div key={field.key} className={field.type === 'textarea' ? 'md:col-span-2' : ''}>
                      <label className="block text-[10px] text-cream/20 mb-1.5 uppercase tracking-wider">{field.label}</label>
                      {field.type === 'text' && (
                        <input
                          value={(pages[schema.id] || {})[field.key] || ''}
                          onChange={(e) => updateField(schema.id, field.key, e.target.value)}
                          placeholder={field.placeholder || ''}
                          className={inputClass}
                        />
                      )}
                      {field.type === 'textarea' && (
                        <textarea
                          value={(pages[schema.id] || {})[field.key] || ''}
                          onChange={(e) => updateField(schema.id, field.key, e.target.value)}
                          placeholder={field.placeholder || ''}
                          rows={3}
                          className={`${inputClass} resize-none`}
                        />
                      )}
                      {field.type === 'image' && (
                        <div>
                          {(pages[schema.id] || {})[field.key] && (
                            <img src={(pages[schema.id] || {})[field.key]} alt="" className="w-full max-h-32 object-contain rounded mb-2 border border-cream/[0.06]" />
                          )}
                          <MediaUploader
                            accept="image"
                            folder="pages"
                            label={`Upload ${field.label.toLowerCase()}`}
                            onUpload={(url) => updateField(schema.id, field.key, url)}
                          />
                          <input
                            value={(pages[schema.id] || {})[field.key] || ''}
                            onChange={(e) => updateField(schema.id, field.key, e.target.value)}
                            placeholder="Or paste URL..."
                            className={`${inputClass} mt-1.5 text-xs`}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <div className="mt-4 flex justify-end">
                  <button
                    onClick={() => savePage(schema.id)}
                    disabled={saving[schema.id]}
                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-gold text-navy text-xs font-semibold rounded-lg hover:bg-gold-light disabled:opacity-50"
                    data-testid={`save-page-${schema.id}`}
                  >
                    {saving[schema.id] ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                    {saved[schema.id] ? 'Saved!' : 'Save'}
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminPages;
